import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Home, Users, BookOpen, FileText, Settings, LogIn, Info, Mail, CalendarDays, FolderOpen, ClipboardCheck, GraduationCap, UserCircle } from 'lucide-react';
import '../styles/Sidebar.css';

export default function Sidebar() {
  // State to hold user role
  const [role, setRole] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false); // NEW
  // Initialize navigate from react-router-dom
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    async function fetchRole() {
      // First, check if we have cached user data
      const cachedUser = localStorage.getItem('user');
      if (cachedUser) {
        try {
          const user = JSON.parse(cachedUser);
          if (user?.role) {
            setRole(user.role);
          }
        } catch (e) {
          // If parsing fails, continue to fetch from server
        }
      }

      const token = localStorage.getItem('token');
      if (!token && !cachedUser) {
        // No token and no cached user - redirect to login
        navigate('/');
        return;
      }

      const requestConfig = {
        params: { _: Date.now() },
        validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
        withCredentials: true, // Ensure cookies are sent if available
      };

      // Always send Bearer token if available (as fallback if cookie isn't sent)
      // This is critical for production where cookies may not work across subdomains
      if (token) {
        requestConfig.headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        };
      }

      try {
        const response = await axios.get('/auth/me', requestConfig);
        if (!isMounted) return;

        // Handle 304 responses - they might not have response.data
        if (response.status === 304) {
          // Use cached user data if available
          if (cachedUser) {
            try {
              const user = JSON.parse(cachedUser);
              if (user?.role) {
                setRole(user.role);
                return;
              }
            } catch (e) {
              // If parsing fails, continue
            }
          }
          // If no cached data and 304, keep existing role state
          return;
        }

        // Update role from response
        const nextRole = response.data?.user?.role;
        if (nextRole) {
          setRole(nextRole);
          // Update cached user data
          if (cachedUser) {
            try {
              const user = JSON.parse(cachedUser);
              user.role = nextRole;
              localStorage.setItem('user', JSON.stringify(user));
            } catch (e) {
              // Ignore update errors
            }
          }
        }
      } catch (error) {
        console.error('Nie udało się pobrać roli użytkownika', error);

        // Only redirect on actual 401/403 authentication errors
        if (error.response?.status === 401 || error.response?.status === 403) {
          // Clear auth data and redirect
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/');
        }
        // For other errors (network, etc.), keep the cached role if available
      }
    }

    fetchRole();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  // Map roles exactly as they są w tokenie
  const getRoleLabel = (role) => {
    switch (role) {
      case 'administrator':
        return 'Administrator';
      case 'student':
        return 'Kursant';
      case 'lecturer':
        return 'Wykładowca';
      default:
        return '';
    }
  };
  const handleNavClick = () => {
    if (window.innerWidth <= 768) {
      setIsMenuOpen(false);
    }
  };


  const handleLogout = async () => {
    try {
      await axios.post('/auth/logout');
    } catch (error) {
      console.error('Błąd podczas wylogowywania', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/');
    }
  };

  return (
    <div className='sidebar'>
      <div className='sidebar-header'>
        <div className='sidebar-logo-icon'>
          <img src="/images/logo.svg" alt="Logo szkoły" className='logo-image' />
        </div>
        <div className='sidebar-header-title'>
          <h3 className='header-title'>PSDiA</h3>
          <p className="header-subtitle">{getRoleLabel(role)}</p>
        </div>

        {/* HAMBURGER – widoczny tylko na mobile (CSS) */}
        <button
          type="button"
          className="sidebar-toggle"
          onClick={() => setIsMenuOpen(prev => !prev)}
        >
          <span className="sidebar-toggle-bar" />
          <span className="sidebar-toggle-bar" />
          <span className="sidebar-toggle-bar" />
        </button>
      </div>

      {/* Wrapper, który będziemy zwijać/rozwijać */}
      <div className={`sidebar-main ${isMenuOpen ? 'open' : ''}`}>
        <nav>
          <NavLink
            to={'/admin/dashboard'}
            className={({ isActive }) => 'main-menu' + (isActive ? ' active' : '')}
            onClick={handleNavClick}
          >
            <Home size={20} />
            <span>Panel Główny</span>
          </NavLink>

          <NavLink
            to={'/admin/management'}
            className={({ isActive }) => 'main-menu' + (isActive ? ' active' : '')}
            onClick={handleNavClick}
          >
            <Users size={20} />
            <span>Zarządzanie Kursantami</span>
          </NavLink>

          <NavLink to={'/admin/calendar'} className={({ isActive }) => 'main-menu' + (isActive ? ' active' : '')}>
            <CalendarDays size={20} />
            <span>Kalendarz</span>
          </NavLink>


          <NavLink
            to={'/admin/courses'}
            className={({ isActive }) => 'main-menu' + (isActive ? ' active' : '')}
            onClick={handleNavClick}
          >
            <BookOpen size={20} />
            <span>Grupy i Kursy</span>
          </NavLink>


          <NavLink
            to={'/admin/attendance'}
            className={({ isActive }) => 'main-menu' + (isActive ? ' active' : '')}
            onClick={handleNavClick}
          >
            <ClipboardCheck size={20} />
            <span>Obecności</span>
          </NavLink>

          <NavLink
            to={'/admin/grades'}
            className={({ isActive }) => 'main-menu' + (isActive ? ' active' : '')}
            onClick={handleNavClick}
          >
            <GraduationCap size={20} />
            <span>Oceny</span>
          </NavLink>



          <NavLink
            to={'/admin/messages'}
            className={({ isActive }) => 'main-menu' + (isActive ? ' active' : '')}
            onClick={handleNavClick}
          >
            <Info size={20} />
            <span>Powiadomienia</span>
          </NavLink>

          <NavLink
            to={'/admin/materials'}
            className={({ isActive }) => 'main-menu' + (isActive ? ' active' : '')}
            onClick={handleNavClick}
          >
            <FolderOpen size={20} />
            <span>Materiały</span>
          </NavLink>

          <NavLink
            to={'/admin/mail'}
            className={({ isActive }) => 'main-menu' + (isActive ? ' active' : '')}
            onClick={handleNavClick}
          >
            <Mail size={20} />
            <span>Wiadomości</span>
          </NavLink>

          <NavLink
            to={'/admin/reports'}
            className={({ isActive }) => 'main-menu' + (isActive ? ' active' : '')}
            onClick={handleNavClick}
          >
            <FileText size={20} />
            <span>Raporty</span>
          </NavLink>

          <NavLink
            to={'/admin/settings'}
            className={({ isActive }) => 'main-menu' + (isActive ? ' active' : '')}
            onClick={handleNavClick}
          >
            <Settings size={20} />
            <span>Ustawienia Systemu</span>
          </NavLink>
          <NavLink
            to={'/admin/account'}
            className={({ isActive }) => 'main-menu' + (isActive ? ' active' : '')}
            onClick={handleNavClick}
          >
            <UserCircle size={20} />
            <span>Moje Konto</span>
          </NavLink>
        </nav>
        {/* WYLOGUJ */}
        <div className='logout-section'>
          <div className='nav-item-logout' onClick={() => { handleLogout(); handleNavClick(); }}>
            <LogIn size={20} />
            <span>Wyloguj</span>
          </div>
        </div>
      </div>
    </div>
  );
}
